import { useEffect, useState } from "react";

import {
  convertViewToNoticeFilters,
  useSavedViews,
} from "components/notice_lists/useNoticeListsPageState";
import { useFilterableNoticeTableState } from "components/notices/useFilterableNoticeTableState";
import { NoticeFilters } from "components/notices/utils";
import {
  NoticeHighlightRequest,
  NoticeHighlightResponse,
} from "lib/generated/app-service-gql/graphql";
import { SupplierMentionType } from "lib/types/graphQLEnums";
import { useSignalSettings } from "../teams/useSignalSettings";
import { useNoticeHighlight } from "./useNoticeHighlight";

export function hasMeaningfulHighlightFilters(filters: NoticeFilters | undefined): boolean {
  if (!filters) return false;
  const hasKeywords = filters.keywords.length > 0;
  const hasText = !!filters.text && filters.text.trim().length > 0;
  const hasHighlightableSuppliers =
    filters.supplierMentionType !== SupplierMentionType.DirectlyAwarded &&
    (filters.supplierGuids?.length ?? 0) > 0;
  return hasKeywords || hasText || hasHighlightableSuppliers;
}

export function convertNoticeFiltersToHighlightRequest(
  procStageId: string,
  filters: NoticeFilters | undefined,
): NoticeHighlightRequest {
  if (!filters) {
    return {
      noticeId: procStageId,
      orgIds: undefined,
      keywords: [],
      highlightSignals: false,
    };
  }
  const keywords = [...filters.keywords, filters.text ?? ""]
    .map((k) => k.trim())
    .filter((k) => k.length > 0);
  // If supplier mention type is directly awarded, we don't want to highlight the suppliers
  const supplierGuids =
    filters.supplierMentionType === SupplierMentionType.DirectlyAwarded
      ? undefined
      : filters.supplierGuids;
  return {
    noticeId: procStageId,
    orgIds: supplierGuids && supplierGuids.length > 0 ? supplierGuids : undefined,
    keywords,
    highlightSignals: false,
  };
}

type NoticeHighlightingResponse = {
  highlightTerms: NoticeHighlightResponse | undefined;
  filters: NoticeFilters | undefined;
};
/**
 * Wrapper function for useNoticeHighlight. it gets the currently actioned
 * notice filters from the url params, converts that into a
 * {@link NoticeHighlightRequest} object to be used in the
 * {@link useNoticeHighlight} hook. Then hey presto we have highlights
 * @param procStageId procurement stage Id of the notice
 * @returns
 */
export function useNoticeHighlighting(procStageId: string): NoticeHighlightingResponse {
  const { noticeTableFilters } = useFilterableNoticeTableState();

  // Using a local state here so the url is not affected
  const [filters, setFilters] = useState<NoticeFilters | undefined>(noticeTableFilters);

  const getFiltersFromSavedViews = !hasMeaningfulHighlightFilters(filters);

  const { data: signals, isLoading: signalsLoading } = useSignalSettings(undefined, {
    enabled: getFiltersFromSavedViews,
  });

  const { selectedView } = useSavedViews(getFiltersFromSavedViews);

  // Use effect triggers if there are no highlight-relevant filters from the URL
  // and instead gets params from saved view filters. Wait for signals to settle
  // so SIGNALS:* tokens in the saved view are expanded before we latch filters.
  useEffect(() => {
    if (selectedView && getFiltersFromSavedViews && !signalsLoading) {
      const savedViewFilters = convertViewToNoticeFilters({
        savedView: selectedView,
        signalSettings: signals,
      });
      setFilters(savedViewFilters);
    }
  }, [getFiltersFromSavedViews, selectedView, setFilters, signals, signalsLoading]);

  const highlightingFilters = convertNoticeFiltersToHighlightRequest(procStageId, filters);

  const { data: highlightTerms } = useNoticeHighlight(highlightingFilters, {
    enabled: hasMeaningfulHighlightFilters(filters),
  });

  return { highlightTerms, filters };
}
